import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useState } from 'react';
import { useLocalSettings } from '@/lib/contexts/LocalSettingsContext';

/**
 * Reactive "has this been dismissed?" preference persisted via LocalSettings
 * (localStorage). Used for banners/callouts that a user can permanently close.
 *
 * Initializes to "not dismissed" so SSR and the first client render match
 * (avoids a hydration mismatch), then reads the persisted value after mount.
 *
 * `hasLoaded` is false until the persisted value has been read on the client.
 * Consumers that must not flash before the stored state is known should wait
 * for `hasLoaded` before rendering.
 *
 * @param key Stable LocalSettings key identifying the dismissible element.
 */
export function useDismissible(key) {
  const $ = _c(15);
  const settings = useLocalSettings();
  const [isDismissed, setIsDismissed] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  let t0;
  let t1;
  if ($[0] !== key || $[1] !== settings) {
    t0 = () => {
      const stored = settings.get(key);
      if (typeof stored === "boolean") {
        setIsDismissed(stored);
      }
      setHasLoaded(true);
    };
    t1 = [settings, key];
    $[0] = key;
    $[1] = settings;
    $[2] = t0;
    $[3] = t1;
  } else {
    t0 = $[2];
    t1 = $[3];
  }
  useEffect(t0, t1);
  let t2;
  if ($[4] !== key || $[5] !== settings) {
    t2 = () => {
      setIsDismissed(true);
      settings.set(key, true);
    };
    $[4] = key;
    $[5] = settings;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const dismiss = t2;
  let t3;
  if ($[7] !== key || $[8] !== settings) {
    t3 = () => {
      setIsDismissed(false);
      settings.remove(key);
    };
    $[7] = key;
    $[8] = settings;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const reset = t3;
  let t4;
  if ($[10] !== dismiss || $[11] !== hasLoaded || $[12] !== isDismissed || $[13] !== reset) {
    t4 = {
      isDismissed,
      hasLoaded,
      dismiss,
      reset
    };
    $[10] = dismiss;
    $[11] = hasLoaded;
    $[12] = isDismissed;
    $[13] = reset;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  return t4;
}